/**
 * Google Sans, and nothing else — mobile/docs/design-language.md §2.
 *
 * Only the family NAMES live here. The actual font files are loaded per
 * platform: mobile registers the TTFs through expo-font (see
 * mobile/src/constants/typography.ts), the web loads subset woff2 through
 * next/font/local. Sharing the names is what keeps a weight from being
 * referenced on one surface and missing on the other.
 */
export const font = {
  regular: 'GoogleSans-Regular',
  medium: 'GoogleSans-Medium',
  semibold: 'GoogleSans-SemiBold',
  bold: 'GoogleSans-Bold',
} as const;

export type FontWeightName = keyof typeof font;

/** CSS `font-weight` for each named face, so the web can ask for one family and
 *  four weights rather than four families. */
export const fontWeight: Record<FontWeightName, number> = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

/** The CSS family name the web registers Google Sans under. */
export const FONT_FAMILY = 'Google Sans';

/**
 * Numeric readouts use the same family with tabular figures rather than a
 * separate monospace. There is one typeface on this product; a second one for
 * numbers was a habit, not a decision.
 */
export const TABULAR_FEATURES = '"tnum" 1, "cv01" 1';

/**
 * The type scale.
 *
 * One ladder, two anchors. Every size on a marketing surface is a named role
 * here; nothing hand-rolls a `clamp()` or a raw px any more.
 *
 * The scale exists because the landing page did hand-roll them — six
 * independent `clamp(min, Nvw, max)` declarations whose minimums were each
 * chosen so that one particular string would not overflow on a phone, with
 * nothing anywhere relating them to each other or to the body text they sit
 * beside. The result was a ladder that read correctly at 1280px and merged
 * into itself at 390px: the closing-CTA heading and the section headings
 * landed 1.6px apart, and the hero fell from 4.0x its subhead to 2.4x. That
 * is not a size bug you fix by nudging six numbers; it is the absence of a
 * scale, so this is a scale.
 *
 * Three rules it encodes:
 *
 *   1. DISPLAY TYPE IS FLUID, TEXT TYPE IS FIXED. Headings interpolate
 *      between the anchors; `lead` and below hold one size everywhere.
 *      Fluid body copy reads as a bug at every width between the anchors and
 *      fights the reader's own font-size setting. The split is also what
 *      makes the hierarchy question answerable at all: exactly one tier moves.
 *   2. BOTH ENDS ARE DESIGNED. `min` is the size at PHONE_PX, `max` the size
 *      at DESKTOP_PX — not a floor someone reached for to stop a wrap. The
 *      phone column is as deliberate as the desktop one, which is the whole
 *      fix.
 *   3. THE INTERPOLATION KEEPS A rem TERM. `fluidType` emits
 *      `clamp(min, <rem> + <vw>, max)` rather than a bare `vw` middle, so a
 *      reader who has raised their browser font size still moves the type.
 *      A pure-vw middle term silently ignores them; the old clamps all had
 *      one.
 *
 * Consumed as CSS custom properties (`--rt-text-<role>`, emitted by
 * css-tokens.ts) and as Tailwind `text-<role>` utilities (globals.css maps
 * them into `@theme`). Invariants are asserted in
 * `web/tests/unit/typeScale.test.ts` — including that the desktop column has
 * not moved, since the scale was introduced under a hard "desktop looks
 * exactly as it does today" constraint.
 */

/** Viewport at which every role sits at its `min`. Below this, type is locked. */
export const TYPE_PHONE_PX = 390;
/** Viewport at which every role sits at its `max`. Above this, type is locked. */
export const TYPE_DESKTOP_PX = 1280;

/** The browser default the rem terms are computed against. */
const ROOT_PX = 16;

export interface TypeStep {
  /** px at TYPE_PHONE_PX. Equal to `max` for the non-fluid text tier. */
  min: number;
  /** px at TYPE_DESKTOP_PX. */
  max: number;
  /** Unitless, so it tracks the size. Display type sets its own; text type
   *  takes reading leading. */
  leading: number;
  /** em. Negative on display type only — large type needs the optical
   *  tightening that text type at 16px does not. */
  tracking: number;
}

/**
 * The fluid display tier first, largest to smallest, then the fixed text tier,
 * likewise. The order is load-bearing: the invariant tests walk adjacent pairs
 * within a tier, so a role inserted out of order fails rather than quietly
 * widening a gap it was meant to sit inside.
 *
 * Two roles are size-siblings of a neighbour and differ only in how they are
 * set: `stat` of `display-3`, `title` of `lead`. That is what a role IS here —
 * a job, not a size. Reusing `display-3` for a counter or `lead` for a card
 * heading is how leading meant for a sentence ends up around something that
 * is not one.
 */
export const typeScale = {
  /** The page's one hero. At most one per route. */
  'display-1': { min: 48, max: 64, leading: 1.04, tracking: -0.025 },
  /** A closing call to action — the second-loudest thing on a page. */
  'display-2': { min: 40, max: 54, leading: 1.06, tracking: -0.025 },
  /** Section headings. The workhorse; most pages use only this one. */
  'display-3': { min: 34, max: 46, leading: 1.08, tracking: -0.025 },
  /** Sub-section headings sitting inside a display-3 section. */
  'display-4': { min: 26, max: 33, leading: 1.12, tracking: -0.025 },
  /** A numeral readout. display-3's sizes, set solid — a counter is a figure,
   *  not a sentence, and heading leading leaves it floating in its own box. */
  stat: { min: 34, max: 46, leading: 1, tracking: -0.025 },
  /** The title at the top of a pane: a guided step's question, an editor
   *  panel's name. Fixed, because a pane does not grow with the viewport. */
  heading: { min: 22, max: 22, leading: 1.2, tracking: -0.015 },
  /** The sentence under a display. One step above body on purpose. */
  lead: { min: 16, max: 16, leading: 1.625, tracking: 0 },
  /** A short heading over a block — a card, a column, an FAQ row. `lead`'s
   *  size set tight, because it is a label and not a sentence. */
  title: { min: 16, max: 16, leading: 1.25, tracking: -0.015 },
  /** Running copy and control labels. */
  body: { min: 15, max: 15, leading: 1.6, tracking: 0 },
  /** Navigation, footers, the second line of a two-line item. */
  'body-sm': { min: 13, max: 13, leading: 1.5, tracking: 0 },
  /** Ordinals and fine print. The floor for anything a reader must read. */
  caption: { min: 12, max: 12, leading: 1.4, tracking: 0 },
  /** Tracked-out uppercase labels. Never a sentence — at 10px it is a tag. */
  overline: { min: 10, max: 10, leading: 1.4, tracking: 0.2 },
} as const satisfies Record<string, TypeStep>;

export type TypeRole = keyof typeof typeScale;

export const TYPE_ROLES = Object.keys(typeScale) as TypeRole[];

/** Trailing zeros dropped, so the emitted CSS reads like something written. */
const num = (n: number) => String(Number(n.toFixed(4)));

/**
 * The role's `font-size`, as a clamp between the two anchors.
 *
 * Linear in the viewport between them and locked outside, with the constant
 * carried in rem so browser zoom still reaches it (rule 3 above). A step whose
 * ends are equal emits a plain rem — a clamp with three identical arms is
 * noise in devtools.
 */
export function fluidType({ min, max }: Pick<TypeStep, 'min' | 'max'>): string {
  if (min === max) return `${num(min / ROOT_PX)}rem`;
  const slope = (max - min) / (TYPE_DESKTOP_PX - TYPE_PHONE_PX);
  const intercept = min - slope * TYPE_PHONE_PX;
  return `clamp(${num(min / ROOT_PX)}rem, ${num(intercept / ROOT_PX)}rem + ${num(slope * 100)}vw, ${num(max / ROOT_PX)}rem)`;
}

/** The role's rendered px at a given viewport — what the clamp resolves to.
 *  Tests and tooling read this rather than re-deriving the arithmetic. */
export function typeSizeAt(role: TypeRole, viewportPx: number): number {
  const { min, max } = typeScale[role];
  if (viewportPx <= TYPE_PHONE_PX) return min;
  if (viewportPx >= TYPE_DESKTOP_PX) return max;
  const t = (viewportPx - TYPE_PHONE_PX) / (TYPE_DESKTOP_PX - TYPE_PHONE_PX);
  return min + t * (max - min);
}

/**
 * The front door: the same ladder, set louder.
 *
 * A homepage is looked at more than it is read — a few words beside a large
 * picture of the product, from further back than a guide is read at. At the
 * content scale above it read small (owner, 2026-09-26: "text is all around
 * too small"). So the front door re-anchors the whole ladder rather than
 * inventing sizes: every role keeps its job (`display-1` is still the page's
 * one hero, `display-3` a section heading, `lead` the line under a display),
 * and only its size moves, only inside `.rt-front-door` (css-tokens.ts emits
 * the scoped block; `@theme inline` resolves `text-<role>` at the element, so
 * the override reaches every utility under it).
 *
 * It holds the same invariants as `typeScale` (typeScale.test.ts runs the
 * ladder checks over both), with one deliberate difference: `lead` and its
 * sibling `title` are fluid here. A front door's lines are one short sentence
 * set at display distance, and a hero this size over a fixed 16px line would
 * fall below 3x on a phone — the exact flatness the base scale exists to
 * prevent.
 */
export const frontDoorScale = {
  'display-1': { min: 52, max: 104, leading: 0.98, tracking: -0.035 },
  'display-2': { min: 42, max: 76, leading: 1.02, tracking: -0.03 },
  'display-3': { min: 36, max: 64, leading: 1.04, tracking: -0.03 },
  'display-4': { min: 28, max: 46, leading: 1.08, tracking: -0.025 },
  stat: { min: 36, max: 64, leading: 1, tracking: -0.03 },
  heading: { min: 24, max: 24, leading: 1.2, tracking: -0.015 },
  lead: { min: 17, max: 21, leading: 1.5, tracking: 0 },
  title: { min: 17, max: 21, leading: 1.2, tracking: -0.015 },
  body: { min: 16, max: 16, leading: 1.6, tracking: 0 },
  'body-sm': { min: 14, max: 14, leading: 1.5, tracking: 0 },
  caption: { min: 13, max: 13, leading: 1.4, tracking: 0 },
  overline: { min: 11, max: 11, leading: 1.4, tracking: 0.2 },
} as const satisfies Record<TypeRole, TypeStep>;

/** `typeSizeAt` for the front door. */
export function frontDoorSizeAt(role: TypeRole, viewportPx: number): number {
  const { min, max } = frontDoorScale[role];
  if (viewportPx <= TYPE_PHONE_PX) return min;
  if (viewportPx >= TYPE_DESKTOP_PX) return max;
  const t = (viewportPx - TYPE_PHONE_PX) / (TYPE_DESKTOP_PX - TYPE_PHONE_PX);
  return min + t * (max - min);
}

